/**
 * Los países cuyo documento de identidad podemos confirmar contra un registro
 * oficial, y qué le pedimos a la persona en cada uno.
 *
 * ESPEJO DELIBERADO de `supabase/functions/_shared/verification/government-registry.ts`.
 * Es una copia porque Deno no puede importar el bundle de la app, igual que
 * pasaba con la regla del teléfono — con una diferencia que importa: aquella se
 * pudo eliminar poniendo a los dos lados a preguntarle a la misma biblioteca,
 * y aquí no hay biblioteca que preguntar, porque a qué endpoint de Verifik va
 * cada país es una decisión nuestra. Lo que sí se puede es que un validador lea
 * los dos archivos y falle cuando discrepen, que es lo que hace
 * `scripts/validate-identity-registries.mjs`.
 *
 * Un país que ofrece la pantalla y el servidor rechaza es un callejón sin
 * salida que nadie puede diagnosticar desde afuera.
 */

/** Qué documento confirma el registro de cada país. */
export type IdentityRegistryKind = 'dui' | 'curp' | 'cedula' | 'driver_license'

export interface IdentityRegistryCountry {
  /** ISO 3166-1 alpha-2. */
  iso2: string
  kind: IdentityRegistryKind
  /** Clave i18n del nombre del documento, tal como lo llama la gente ahí. */
  documentLabelKey: string
  /**
   * Cómo se escribe el número ahí, como ejemplo dentro del campo.
   *
   * Estaba fijo en "00000000-0", que es un DUI salvadoreño: a alguien en
   * México se le pedía un CURP mostrándole el formato de otro país. El
   * ejemplo es parte de la pregunta, no decoración.
   */
  numberPlaceholder: string
  /**
   * Si el registro necesita la fecha de nacimiento además del número.
   *
   * Es el valor POR PAÍS, y una jurisdicción puede contradecirlo. Léelo con
   * `identityRequiresDateOfBirth()`, nunca directo: bajo `US` conviven Florida,
   * que consulta con el número solo, y Kansas, que pide cuatro campos.
   */
  requiresDateOfBirth: boolean
  /**
   * Si el registro es sub-nacional y hay que preguntar la provincia o el estado.
   *
   * Estados Unidos y Canadá no tienen registro nacional de licencias: cada
   * jurisdicción lleva el suyo. Sin la provincia no hay ninguna consulta que
   * hacer, así que preguntarla no es un campo de más — es el campo.
   */
  requiresRegion: boolean
  /**
   * Las jurisdicciones que el registro atiende de verdad.
   *
   * Espejo de `CANADIAN_PROVINCES` / `US_STATES` en
   * `supabase/functions/_shared/verification/government-registry.ts`, cotejado
   * por `scripts/validate-identity-registries.mjs`. Existe para que la pantalla
   * OFREZCA las que funcionan en vez de dejar escribir cualquiera: un campo de
   * texto libre acá invita a teclear "California", que el servidor solo puede
   * contestar con "no podemos confirmarlo automáticamente" después de haber
   * hecho subir las dos fotos.
   */
  supportedRegions?: readonly {
    readonly value: string
    readonly label: string
    /** Si ESTA jurisdicción pide la fecha. Omitido = lo que diga el país. */
    readonly requiresDateOfBirth?: boolean
    /** Si ESTA jurisdicción pide nombre y apellido además del número. */
    readonly requiresName?: boolean
  }[]
}

export const IDENTITY_REGISTRY_COUNTRIES: readonly IdentityRegistryCountry[] = [
  {
    iso2: 'SV',
    kind: 'dui',
    numberPlaceholder: '00000000-0',
    documentLabelKey: 'verification.document_type_dui',
    requiresDateOfBirth: true,
    requiresRegion: false,
  },
  {
    iso2: 'MX',
    kind: 'curp',
    numberPlaceholder: 'AAAA000000HAAAAA00',
    documentLabelKey: 'verification.document_type_curp',
    // La CURP lleva la fecha de nacimiento codificada en sus posiciones 5-10.
    // Pedirla otra vez sería pedir dos veces lo mismo y dar una forma más de
    // que no coincida.
    requiresDateOfBirth: false,
    requiresRegion: false,
  },
  {
    iso2: 'CR',
    kind: 'cedula',
    numberPlaceholder: '0-0000-0000',
    documentLabelKey: 'verification.document_type_cedula',
    requiresDateOfBirth: false,
    requiresRegion: false,
  },
  {
    iso2: 'HN',
    kind: 'cedula',
    documentLabelKey: 'verification.document_type_dni',
    numberPlaceholder: '0801-1990-12345',
    requiresDateOfBirth: false,
    requiresRegion: false,
  },
  {
    iso2: 'GT',
    kind: 'cedula',
    documentLabelKey: 'verification.document_type_dpi',
    numberPlaceholder: '2222624190903',
    requiresDateOfBirth: true,
    requiresRegion: false,
  },
  {
    iso2: 'PA',
    kind: 'cedula',
    documentLabelKey: 'verification.document_type_cedula',
    numberPlaceholder: '8-1251-1829',
    requiresDateOfBirth: false,
    requiresRegion: false,
  },
  {
    iso2: 'US',
    kind: 'driver_license',
    numberPlaceholder: '',
    documentLabelKey: 'verification.document_type_driver_license',
    // Florida consulta con el número de licencia solo. La exigencia vive en la
    // jurisdicción que la tiene, no en el país: pedirle la fecha a todo Estados
    // Unidos declinaba a Florida antes de salir a la red.
    requiresDateOfBirth: false,
    requiresRegion: true,
    // Verifik publica licencia de conducir para Florida y Kansas, y para
    // ningún otro estado. La lista es corta porque la cobertura es corta.
    supportedRegions: [
      { value: 'florida', label: 'Florida' },
      // El único de los tres que pide los cuatro campos: número, fecha, nombre
      // y apellido.
      { value: 'kansas', label: 'Kansas', requiresDateOfBirth: true, requiresName: true },
    ],
  },
  {
    iso2: 'CA',
    kind: 'driver_license',
    numberPlaceholder: '',
    documentLabelKey: 'verification.document_type_driver_license',
    // Ontario consulta con el número solo; su doc lista un único parámetro
    // obligatorio.
    requiresDateOfBirth: false,
    requiresRegion: true,
    supportedRegions: [{ value: 'ontario', label: 'Ontario' }],
  },
] as const

const BY_ISO2 = new Map(IDENTITY_REGISTRY_COUNTRIES.map((entry) => [entry.iso2, entry]))

/** El registro de un país, o null si no tenemos uno. */
export function identityRegistryFor(
  iso2: string | null | undefined,
): IdentityRegistryCountry | null {
  if (!iso2) return null
  return BY_ISO2.get(iso2.trim().toUpperCase()) ?? null
}

/** El país por defecto: el de casa. */
/**
 * Si hay que pedir la fecha de nacimiento para ESTA jurisdicción.
 *
 * Espejo de `requiresDateOfBirthFor()` en
 * `supabase/functions/_shared/verification/government-registry.ts`, cotejado
 * por `scripts/validate-identity-registries.mjs`. Si la pantalla y el servidor
 * discrepan, o se le pide a alguien un dato que su registro no usa, o la
 * consulta sale incompleta y se cobra igual.
 */
export function identityRequiresDateOfBirth(
  registry: IdentityRegistryCountry | null | undefined,
  region: string | null | undefined,
): boolean {
  if (!registry) return true
  const key = region?.trim().toLowerCase()
  const jurisdiction = key
    ? registry.supportedRegions?.find((entry) => entry.value.toLowerCase() === key)
    : undefined
  return jurisdiction?.requiresDateOfBirth ?? registry.requiresDateOfBirth
}

/**
 * Si hay que pedir nombre y apellido para confirmar.
 *
 * El nombre solo lo exige el flujo SIN registro (pasaporte/documento extranjero,
 * donde el nombre ES la identidad) y Kansas, que lo manda como parámetro de la
 * consulta. Para DUI, CURP, cédula, etc. la consulta va por número y fecha: pedir
 * el nombre bloquearía a alguien cuyo nombre el OCR no leyó por un reflejo,
 * aunque el número y la fecha estén perfectos.
 */
export function identityRequiresName(
  registry: IdentityRegistryCountry | null | undefined,
  region: string | null | undefined,
): boolean {
  if (!registry) return true
  const key = region?.trim().toLowerCase()
  const jurisdiction = key
    ? registry.supportedRegions?.find((entry) => entry.value.toLowerCase() === key)
    : undefined
  return jurisdiction?.requiresName ?? false
}

export const DEFAULT_IDENTITY_COUNTRY_ISO2 = 'SV'

/**
 * ¿Tiene forma de número de ese registro?
 *
 * La misma pregunta que hace el servidor antes de gastar la consulta
 * (`normalizeNumber` en government-registry.ts), hecha acá para no mandar a
 * alguien a la pantalla siguiente con un número que el registro va a rechazar
 * por su forma. No es la validación autoritativa —esa está en el servidor— es
 * la que evita el viaje.
 */
export function isPlausibleRegistryNumber(
  registry: IdentityRegistryCountry | null,
  raw: string | null | undefined,
): boolean {
  const value = (raw ?? '').trim()
  if (!value) return false
  switch (registry?.kind) {
    case 'dui':
      return value.replace(/[^0-9]/g, '').length === 9
    case 'curp':
      // 4 letras, 6 dígitos de fecha, sexo, 2 letras de entidad, 3 consonantes,
      // y 2 de verificación. Se comprueba entera porque el OCR confunde O/0 y
      // I/1 justo en las posiciones donde la CURP alterna letras y dígitos.
      return /^[A-Z]{4}\d{6}[HM][A-Z]{2}[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9]\d$/.test(
        value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
      )
    case 'cedula': {
      const digits = value.replace(/[^0-9]/g, '')
      return digits.length === 9 || (digits.length === 10 && digits.startsWith('0'))
    }
    case 'driver_license': {
      // Cada jurisdicción tiene su formato y no hay uno común. Solo se acota el
      // largo: inventar una regla por estado sería rechazar licencias válidas
      // por una forma que nos imaginamos.
      const compact = value.toUpperCase().replace(/[^A-Z0-9]/g, '')
      return compact.length >= 5 && compact.length <= 25
    }
    default:
      // Sin registro (pasaporte, país que no cubrimos): cualquier número no
      // vacío sirve, porque no hay nada contra qué comprobarlo y el documento
      // va a revisión de todos modos.
      return true
  }
}
